import type {
  User, Game, Achievement, UserAchievement, Assignment, Unit, UnitStudent, Stat,
} from '@/types';
import type { Activity, ActivityResponse } from '@ludolio/shared-units';

// The email that triggers demo mode from the Login screen.
export const DEMO_EMAIL = 'demo@ludolio.com';

const DEMO_ORG_ID = 'demo-org-0000';
const DEMO_USER_ID = 'demo-user-0000';

const iso = (daysAgo: number) =>
  new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000).toISOString();

const makeId = (prefix: string, n: number) => `${prefix}-${n.toString().padStart(4, '0')}`;

// ---------- Core entities ----------

const demoUser: User = {
  id: DEMO_USER_ID,
  email: DEMO_EMAIL,
  firstName: 'Demo',
  lastName: 'Librarian',
  orgUsername: 'demo.librarian',
  role: 'librarian',
  orgId: DEMO_ORG_ID,
  createdAt: iso(120),
};

const demoOrg = {
  id: DEMO_ORG_ID,
  name: 'Ludolio Demo University',
  isSubscribed: true,
  region: 'AU' as string | null,
  staffEmailDomains: ['demo-staff.ludolio.com'],
  studentEmailDomains: ['demo-students.ludolio.com'],
};

const demoOrgStats = {
  totalRegisteredUsers: 16,
  totalTeachers: 4,
  totalStudents: 10,
  totalLibrarians: 2,
  totalUnits: 4,
  activeUnits: 3,
  archivedUnits: 1,
  totalPlayTimeSeconds: 186_420,
  sharedDeviceCount: 5,
  studentsUsingSharedDevices: 7,
};

const games: Game[] = [
  { id: makeId('game', 1), appId: 1001, title: 'Mechanic Forge',   description: 'A systems design sandbox where students tune core loops, reward cadence, and difficulty pacing.', publisher: 'Ludolio Studio', version: '1.4.2', executablePath: 'MechanicForge.exe',   isPublished: true,  createdAt: iso(200), updatedAt: iso(12) },
  { id: makeId('game', 2), appId: 1002, title: 'Level Lab',        description: 'A level design game focused on pathing, encounter flow, sightlines, and tutorial spaces.',        publisher: 'Ludolio Studio', version: '0.9.7', executablePath: 'LevelLab.exe',        isPublished: true,  createdAt: iso(160), updatedAt: iso(30) },
  { id: makeId('game', 3), appId: 1003, title: 'Narrative Nexus',  description: 'A narrative design experience for branching dialogue, quest structure, and player choice.',       publisher: 'Ludolio Studio', version: '2.0.1', executablePath: 'NarrativeNexus.exe',  isPublished: true,  createdAt: iso(140), updatedAt: iso(6)  },
  { id: makeId('game', 4), appId: 1004, title: 'Playtest Studio',  description: 'A playtesting toolkit for tutorial UX, feedback loops, and retention friction analysis.',          publisher: 'Partner Press',  version: '1.1.0', executablePath: 'PlaytestStudio.exe',  isPublished: false, createdAt: iso(90),  updatedAt: iso(45) },
];

const teacherUsers: User[] = [
  { id: makeId('teacher', 1), email: 'casey.morgan@demo-staff.ludolio.com', firstName: 'Casey',  lastName: 'Morgan',  role: 'teacher', orgId: DEMO_ORG_ID, createdAt: iso(95) },
  { id: makeId('teacher', 2), email: 'jordan.lee@demo-staff.ludolio.com',    firstName: 'Jordan', lastName: 'Lee',     role: 'teacher', orgId: DEMO_ORG_ID, createdAt: iso(80) },
  { id: makeId('teacher', 3), email: 'avery.patel@demo-staff.ludolio.com',   firstName: 'Avery',  lastName: 'Patel',   role: 'staff',   orgId: DEMO_ORG_ID, createdAt: iso(60) },
  { id: makeId('teacher', 4), email: 'sam.nguyen@demo-staff.ludolio.com',    firstName: 'Sam',    lastName: 'Nguyen',  role: 'teacher', orgId: DEMO_ORG_ID, createdAt: iso(40) },
];

const librarianUsers: User[] = [
  { id: makeId('librarian', 1), email: 'robin.shaw@demo-staff.ludolio.com', firstName: 'Robin', lastName: 'Shaw', role: 'librarian', orgId: DEMO_ORG_ID, createdAt: iso(70) },
];

const teacherEmails = [
  { id: makeId('temail', 1), email: 'casey.morgan@demo-staff.ludolio.com', name: 'Casey Morgan', role: 'teacher' as const, isUsed: true,  userId: makeId('teacher', 1) },
  { id: makeId('temail', 2), email: 'jordan.lee@demo-staff.ludolio.com',    name: 'Jordan Lee',   role: 'teacher' as const, isUsed: true,  userId: makeId('teacher', 2) },
  { id: makeId('temail', 3), email: 'avery.patel@demo-staff.ludolio.com',   name: 'Avery Patel',  role: 'staff'   as const, isUsed: true,  userId: makeId('teacher', 3) },
  { id: makeId('temail', 4), email: 'sam.nguyen@demo-staff.ludolio.com',    name: 'Sam Nguyen',   role: 'teacher' as const, isUsed: true,  userId: makeId('teacher', 4) },
  { id: makeId('temail', 5), email: 'riley.chen@demo-staff.ludolio.com',    name: 'Riley Chen',   role: 'teacher' as const, isUsed: false, userId: null },
  { id: makeId('temail', 6), email: 'taylor.kim@demo-staff.ludolio.com',    name: 'Taylor Kim',   role: 'staff'   as const, isUsed: false, userId: null },
];

const librarianEmails = [
  {
    id: makeId('lemail', 1),
    email: 'morgan.price@demo-staff.ludolio.com',
    name: 'Morgan Price',
  },
];

const studentFirstNames = ['Alex', 'Blake', 'Chris', 'Dana', 'Elliot', 'Fin', 'Gale', 'Harper', 'Indie', 'Jamie'];
const studentLastNames  = ['Adams', 'Brooks', 'Clark', 'Diaz', 'Evans', 'Flores', 'Garza', 'Hale', 'Irwin', 'Jones'];

const students: User[] = studentFirstNames.map((first, i) => ({
  id: makeId('student', i + 1),
  email: `${first.toLowerCase()}.${studentLastNames[i].toLowerCase()}@demo-students.ludolio.com`,
  firstName: first,
  lastName: studentLastNames[i],
  role: 'student',
  orgId: DEMO_ORG_ID,
  createdAt: iso(90 - i * 5),
}));

const pendingStudentEmails = [
  'morgan.wu@demo-students.ludolio.com',
  'quinn.reed@demo-students.ludolio.com',
];

const units: Unit[] = [
  { id: makeId('unit', 1), orgId: DEMO_ORG_ID, code: 'GAME101', name: 'Introduction to Game Design',  year: 2025, semester: 'semester_1', isArchived: false, createdAt: iso(100), updatedAt: iso(20), organization: { id: DEMO_ORG_ID, name: demoOrg.name } },
  { id: makeId('unit', 2), orgId: DEMO_ORG_ID, code: 'GAME210', name: 'Level Design Studio',          year: 2025, semester: 'semester_1', isArchived: false, createdAt: iso(85),  updatedAt: iso(14), organization: { id: DEMO_ORG_ID, name: demoOrg.name } },
  { id: makeId('unit', 3), orgId: DEMO_ORG_ID, code: 'GAME240', name: 'Narrative Design for Games',   year: 2025, semester: 'semester_2', isArchived: false, createdAt: iso(55),  updatedAt: iso(5),  organization: { id: DEMO_ORG_ID, name: demoOrg.name } },
  { id: makeId('unit', 4), orgId: DEMO_ORG_ID, code: 'GAME190', name: 'Foundations of Interactive Media', year: 2024, semester: 'semester_2', isArchived: true, createdAt: iso(300), updatedAt: iso(80), organization: { id: DEMO_ORG_ID, name: demoOrg.name } },
];

// unitId -> studentIds, unitId -> gameIds (simple lookup tables)
const unitStudentIds: Record<string, string[]> = {
  [units[0].id]: students.slice(0, 6).map(s => s.id),
  [units[1].id]: students.slice(3, 9).map(s => s.id),
  [units[2].id]: students.slice(5, 10).map(s => s.id),
  [units[3].id]: students.slice(0, 4).map(s => s.id),
};

const unitGameIds: Record<string, string[]> = {
  [units[0].id]: [games[0].id, games[3].id],
  [units[1].id]: [games[1].id],
  [units[2].id]: [games[2].id, games[0].id],
  [units[3].id]: [games[3].id],
};

const unitStaffIds: Record<string, string[]> = {
  [units[0].id]: [teacherUsers[0].id, teacherUsers[1].id, teacherUsers[3].id, DEMO_USER_ID, librarianUsers[0].id],
  [units[1].id]: [teacherUsers[1].id, librarianUsers[0].id],
  [units[2].id]: [teacherUsers[0].id, teacherUsers[3].id, DEMO_USER_ID],
  [units[3].id]: [teacherUsers[2].id],
};

const pendingUnitEnrollments: Array<{
  id: string;
  unitId: string;
  email: string;
  createdAt: string;
}> = [];

const toUnitStudent = (studentId: string, idx: number): UnitStudent => {
  const s = students.find(x => x.id === studentId)!;
  return {
    id: s.id, email: s.email, firstName: s.firstName, lastName: s.lastName,
    role: 'student', createdAt: s.createdAt,
    status: idx % 5 === 4 ? 'pending' : 'enrolled',
    totalPlayingHours: Number((1.5 + idx * 0.7).toFixed(1)),
    lastActive: iso(idx + 1),
  };
};

const achievements: Achievement[] = games.flatMap((g, gi) => [
  { id: makeId(`ach-${gi}`, 1), gameId: g.id, achievementId: `${g.id}_first_prototype`, name: 'First Prototype',  description: 'Ship your first playable design prototype.',              points: 10, isSecret: false, createdAt: iso(150) },
  { id: makeId(`ach-${gi}`, 2), gameId: g.id, achievementId: `${g.id}_iteration`,       name: 'Iteration Ready',  description: 'Complete a full iteration cycle with playtest feedback.', points: 25, isSecret: false, createdAt: iso(150) },
  { id: makeId(`ach-${gi}`, 3), gameId: g.id, achievementId: `${g.id}_design_polish`,   name: 'Design Polish',    description: 'Ship a polished design feature that passes review.',      points: 50, isSecret: false, createdAt: iso(140) },
  { id: makeId(`ach-${gi}`, 4), gameId: g.id, achievementId: `${g.id}_hidden_mechanic`, name: 'Hidden Mechanic',  description: 'Discover an emergent interaction in your design.',        points: 75, isSecret: true,  createdAt: iso(130) },
]);

const activities: Array<Omit<Activity, 'game' | 'achievement'>> = [
  {
    id: makeId('activity', 1),
    unitId: units[0].id,
    gameId: games[0].id,
    achievementId: achievements[0].id,
    createdBy: teacherUsers[0].id,
    title: 'Prototype reflection',
    description: 'Reflect on the first playable prototype.',
    type: 'short_answer',
    config: { questions: [{ question: 'What changed most after your first playtest?' }] },
  },
  {
    id: makeId('activity', 2),
    unitId: units[1].id,
    gameId: games[1].id,
    achievementId: achievements[4].id,
    createdBy: teacherUsers[1].id,
    title: 'Level flow check',
    type: 'quiz',
    config: {
      questions: [{
        question: 'Which element best supports player wayfinding?',
        options: ['Landmarks', 'Random encounters', 'Hidden exits'],
        correctAnswers: ['Landmarks'],
      }],
    },
  },
];

const activityResponses: ActivityResponse[] = [
  {
    id: makeId('response', 1),
    activityId: activities[0].id,
    studentId: students[0].id,
    unitId: units[0].id,
    response: {
      answers: [{ answer: 'I simplified the controls after observing hesitation.' }],
    },
    submittedAt: iso(2),
    student: { id: students[0].id, email: students[0].email, name: `${students[0].firstName} ${students[0].lastName}` },
  },
];

// Spread some unlocks across students and games.
const userAchievements: UserAchievement[] = students.flatMap((s, si) =>
  games.slice(0, 3).flatMap((g, gi) => {
    const count = (si + gi) % 4; // 0..3 achievements per (student, game)
    return achievements
      .filter(a => a.gameId === g.id)
      .slice(0, count)
      .map((a, ai) => ({
        id: makeId(`ua-${si}-${gi}`, ai + 1),
        userId: s.id, gameId: g.id, gameTitle: g.title,
        achievementId: a.achievementId,
        unlockedAt: iso(1 + si + gi * 2 + ai),
        achievement: a,
      }));
  })
);

const assignments: Assignment[] = students.slice(0, 8).flatMap((s, si) =>
  games.slice(0, 2).map((g, gi) => ({
    id: makeId(`asn-${si}`, gi + 1),
    studentId: s.id, gameId: g.id, assignedBy: DEMO_USER_ID,
    startDate: iso(30 + gi), endDate: iso(-30 + gi),
    isActive: true, createdAt: iso(30 + gi), updatedAt: iso(5 + gi),
    game: g, student: s,
  }))
);

const gameStats: Stat[] = games.flatMap((g, gi) => [
  { id: makeId(`stat-${gi}`, 1), gameId: g.id, statId: 'total_score',    name: 'Total Score',    type: 'INT',   incrementOnly: true,  defaultValue: '0', aggregated: true,  createdAt: iso(150) },
  { id: makeId(`stat-${gi}`, 2), gameId: g.id, statId: 'levels_cleared', name: 'Levels Cleared', type: 'INT',   incrementOnly: true,  defaultValue: '0', aggregated: true,  createdAt: iso(150) },
  { id: makeId(`stat-${gi}`, 3), gameId: g.id, statId: 'avg_accuracy',   name: 'Average Accuracy', type: 'FLOAT', incrementOnly: false, defaultValue: '0', aggregated: false, createdAt: iso(140) },
]);

// ---------- Shape ----------

export interface DemoState {
  currentUser: User;
  org: typeof demoOrg;
  orgStats: typeof demoOrgStats;
  autoEnableNewGames: boolean;
  games: Game[];
  teachers: User[];
  librarians: User[];
  teacherEmails: typeof teacherEmails;
  librarianEmails: typeof librarianEmails;
  students: User[];
  pendingStudentEmails: string[];
  units: Unit[];
  unitStudentIds: Record<string, string[]>;
  unitGameIds: Record<string, string[]>;
  unitStaffIds: Record<string, string[]>;
  pendingUnitEnrollments: Array<{
    id: string;
    unitId: string;
    email: string;
    createdAt: string;
  }>;
  achievements: Achievement[];
  activities: Array<Omit<Activity, 'game' | 'achievement'>>;
  activityResponses: ActivityResponse[];
  userAchievements: UserAchievement[];
  assignments: Assignment[];
  gameStats: Stat[];
}

export function createDemoState(): DemoState {
  // Deep clone so mutations in the demo session never leak back into module-level constants.
  return JSON.parse(JSON.stringify({
    currentUser: demoUser,
    org: demoOrg,
    orgStats: demoOrgStats,
    autoEnableNewGames: true,
    games,
    teachers: teacherUsers,
    librarians: librarianUsers,
    teacherEmails,
    librarianEmails,
    students,
    pendingStudentEmails,
    units,
    unitStudentIds,
    unitGameIds,
    unitStaffIds,
    pendingUnitEnrollments,
    achievements,
    activities,
    activityResponses,
    userAchievements,
    assignments,
    gameStats,
  }));
}

export const demoHelpers = { toUnitStudent };
